// Mail — full client: list, filters, bulk, detail, compose, labels, signature.
//
// Layout: the global Rail + SubNav already give us the folder selector via
// `?filter=`.  Inside this page we render a Gmail-like toolbar + list, with a
// slide-in detail pane and a floating compose modal.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setActiveMailbox } from "../../shared/api/client";
import { Empty, Loading } from "../../shared/components/Empty";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import "./mail.css";
import type { FolderCounts, Mail, MailContact, MailDetail, MailFolder, MailLabel, ReceiptSummary } from "./types";
import { FOLDER_LABELS, flattenFolders, fmtDate, fmtFullDate, formatBytes, quoteOriginal } from "./types";
import { useAddressNames } from "./useAddressNames";
import { cachedGet, invalidateMailCache } from "./mailCache";
import { MailDetailPane } from "./MailDetail";
import { MailSettings } from "./MailSettings";
import { OrgPicker } from "../../shared/org/OrgPicker";

function initials(addr: string): string {
  const local = addr.split("@")[0];
  return local.slice(0, 2).toUpperCase();
}

function colorOf(addr: string): string {
  let hash = 0;
  for (let i = 0; i < addr.length; i++) hash = (hash * 31 + addr.charCodeAt(i)) & 0xffffffff;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 52%)`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

// Standalone HTML document for 인쇄 / 새 창에서 열기.
function buildMailDoc(m: MailDetail): string {
  const when = fmtFullDate(m.received_at || m.sent_at || m.created_at);
  const meta = [
    `<div><b>보낸사람</b> ${escapeHtml(m.from_addr)}</div>`,
    `<div><b>받는사람</b> ${escapeHtml(m.to_addrs.join(", ") || "-")}</div>`,
    m.cc_addrs.length ? `<div><b>참조</b> ${escapeHtml(m.cc_addrs.join(", "))}</div>` : "",
    `<div><b>날짜</b> ${escapeHtml(when)}</div>`,
  ].join("");
  const body = m.body_html || `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${escapeHtml(m.body_text || "")}</pre>`;
  const atts = m.attachments?.length
    ? `<div class="atts"><b>첨부 ${m.attachments.length}개</b> ${m.attachments.map((a) => escapeHtml(a.file_name)).join(", ")}</div>` : "";
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>${escapeHtml(m.subject || "(제목 없음)")}</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Malgun Gothic',sans-serif;color:#1a1a1a;margin:32px;line-height:1.65}
h1{font-size:20px;margin:0 0 14px}.meta{font-size:13px;color:#555;border-bottom:1px solid #e2e2e2;padding-bottom:14px;margin-bottom:18px}
.meta div{margin:3px 0}.meta b{display:inline-block;min-width:62px;color:#888;font-weight:600}.content img{max-width:100%}
.atts{font-size:12px;color:#666;border-top:1px solid #eee;margin-top:24px;padding-top:12px}</style></head>
<body><h1>${escapeHtml(m.subject || "(제목 없음)")}</h1><div class="meta">${meta}</div><div class="content">${body}</div>${atts}</body></html>`;
}

type Filter = {
  unread: boolean;
  starred: boolean;
  important: boolean;
  has_attachments: boolean;
  failed: boolean;
  label_id: number | null;
};

const EMPTY_FILTER: Filter = {
  unread: false, starred: false, important: false, has_attachments: false, failed: false, label_id: null,
};

type SortField = "date" | "from" | "subject" | "size" | "unread";
type SortDir = "asc" | "desc";
// 발신자 표시 이름: "이름 <a@b>" → 이름, "a@b" → a (메일 주소 전체는 우클릭/툴팁으로 확인)
function senderName(addr: string): string {
  const m = addr.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>/);
  if (m && m[1].trim()) return m[1].trim();
  return addr.split("@")[0] || addr;
}

// 발신자 이메일만 추출 ("이름 <a@b>" → a@b)
function senderEmail(addr: string): string {
  const m = addr.match(/<([^>]+)>/);
  return (m ? m[1] : addr).trim();
}

// 컬럼 헤더(클릭 정렬)
function ColHead({ label, field, num, sortField, sortDir, onSort }: {
  label: string; field: SortField; num?: boolean;
  sortField: SortField; sortDir: SortDir; onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <button className={`col ${num ? "num" : ""} ${active ? "active" : ""}`} onClick={() => onSort(field)} title={`${label}순 정렬`}>
      {label}{active && <span className="arr">{sortDir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}

export default function MailPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  // 기본 화면은 전체 메일함(받은 메일함 + 사용자 폴더). 받은 메일함만 보이면 규칙으로
  // 폴더에 들어간 메일이 안 보여 놓친다(2026-09-12 운영 요청).
  const folder = (params.get("filter") || "RECEIVED").toUpperCase();
  const labelParam = params.get("label");   // set when a label is picked in the sidebar tree
  const mbxParam = params.get("mbx");        // set when "열기" from the 공용 편지함 screen

  const [items, setItems] = useState<Mail[] | null>(null);
  // 보낸메일함 목록의 '읽음 n/m'(수신 확인 요약). 목록이 바뀔 때마다 한 번에 받아 온다.
  const [receiptSum, setReceiptSum] = useState<Record<number, ReceiptSummary>>({});
  // 본문 미리보기. 목록에 한 줄로 보여 주고, 행에 마우스를 올리면 더 긴 카드를 띄운다.
  // 메일을 열면 읽음으로 바뀌고 보낸 사람에게 수신확인까지 남으므로, 열지 않고 내용을
  // 가늠할 방법이 따로 있어야 한다.
  const [previewOn, setPreviewOn] = useState<boolean>(() => localStorage.getItem("mail-preview") !== "off");
  const [peek, setPeek] = useState<{ mail: Mail; x: number; y: number } | null>(null);
  const peekTimer = useRef<number | null>(null);
  const clearPeek = useCallback(() => {
    if (peekTimer.current) { window.clearTimeout(peekTimer.current); peekTimer.current = null; }
    setPeek(null);
  }, []);
  // 스치듯 지나가도 카드가 뜨면 성가시다 - 0.55초 머무를 때만 띄운다.
  const startPeek = useCallback((m: Mail, e: React.MouseEvent) => {
    if (!previewOn || !m.preview) return;
    const x = e.clientX, y = e.clientY;
    if (peekTimer.current) window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => setPeek({ mail: m, x, y }), 550);
  }, [previewOn]);
  // 목록을 굴리거나 Esc 를 누르면 닫는다(마우스가 행 밖으로 안 나가도).
  useEffect(() => {
    if (!peek) return;
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") clearPeek(); };
    window.addEventListener("scroll", clearPeek, true);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("scroll", clearPeek, true); window.removeEventListener("keydown", onKey); };
  }, [peek, clearPeek]);
  useEffect(() => clearPeek, [clearPeek]);
  useEffect(() => {
    if (folder !== "SENT" || !items || !items.length) { setReceiptSum({}); return; }
    const ids = items.filter((m) => m.folder === "SENT").map((m) => m.id);
    if (!ids.length) { setReceiptSum({}); return; }
    let alive = true;
    api.get<Record<number, ReceiptSummary>>(`/mail/receipts/summary?ids=${ids.join(",")}${mbxParam ? `&mailbox=${mbxParam}` : ""}`)
      .then((r) => { if (alive) setReceiptSum(r.data || {}); })
      .catch(() => { if (alive) setReceiptSum({}); });
    return () => { alive = false; };
  }, [folder, items, mbxParam]);
  const [counts, setCounts] = useState<FolderCounts | null>(null);
  const [labels, setLabels] = useState<MailLabel[]>([]);
  const [sel, setSel] = useState<MailDetail | null>(null);
  // 모바일에서 상세는 목록을 완전히 덮는다(.mail-detail 이 100% 폭 오버레이).
  // 그래서 다른 메일을 보려면 읽던 것을 닫아야 했다. 잠깐 목록만 들춰 보는 상태를 둔다.
  const [peekList, setPeekList] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<Filter>(EMPTY_FILTER);
  const [search, setSearch] = useState("");
  const [searchTrigger, setSearchTrigger] = useState("");
  const [senderQuery, setSenderQuery] = useState("");   // "이 사람이 보낸 메일" 정확 필터
  const [customFolders, setCustomFolders] = useState<MailFolder[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 5건 이상 이동 시 자동 분류 규칙 추가 제안 팝업
  const [autoClassify, setAutoClassify] = useState<{ folder: string; senders: string[]; count: number } | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number; mail: Mail } | null>(null);
  // 메일함은 **항상 최신순**으로 연다(2026-09-16 요청). 예전엔 한 번 누른 정렬(발신자순·오래된순)을
  // localStorage 에 남겨 다음에 열어도, 다른 메일함으로 옮겨도 그대로였다 — 새 메일이 맨 위에
  // 안 보여 "메일이 안 온다"로 보이기 쉽다. 열 머리글 정렬은 그 자리에서만 잠깐 쓰고 저장하지 않는다.
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  useEffect(() => { try { localStorage.removeItem("mail-sort-field"); localStorage.removeItem("mail-sort-dir"); } catch { /* 무시 */ } }, []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(() => Number(localStorage.getItem("mail-page-size")) || 50);
  const [total, setTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const emlInputRef = useRef<HTMLInputElement>(null);
  const reloadKey = useRef(0);
  const detailPushed = useRef(false);   // 상세보기 열 때 history 항목을 넣었는지

  // 편지함 전환: null = 내 메일함, 그 외 = 공용/다른 사람 메일함의 백킹 사용자 id
  type Mbx = { user_id: number; label: string; kind: string; alias: string };
  const [mailbox, setMailbox] = useState<number | null>(null);
  const [mailboxes, setMailboxes] = useState<Mbx[]>([]);
  const [canViewAll, setCanViewAll] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const pickUserMailbox = (u: { id: number; name: string }) => {
    setMailboxes((prev) => prev.some((x) => x.user_id === u.id)
      ? prev : [...prev, { user_id: u.id, label: `${u.name} 메일함`, kind: "user", alias: "" }]);
    setMailbox(u.id);
    setOrgOpen(false);
  };

  const me = useAuth((s) => s.user);
  const isSuperAdmin = !!me?.roles?.includes("SUPER_ADMIN");   // 사용자 메일함 열람은 최고관리자만
  const myAddr = me ? `${me.username}@timple.me` : "";
  const activeMbx = mailbox != null ? mailboxes.find((x) => x.user_id === mailbox) : null;
  const onSortTab = (field: SortField) => {
    if (field === sortField) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field); setSortDir(field === "from" || field === "subject" ? "asc" : "desc");
    }
  };
  // 메일함을 바꾸면 정렬도 최신순으로 되돌린다.
  useEffect(() => { setSortField("date"); setSortDir("desc"); }, [folder, labelParam, mailbox]);

  // Compose now lives on its own page (/mail/compose); pass reply/forward/draft via state.
  const openCompose = (initial?: any) => nav("/mail/compose", initial ? { state: { initial } } : undefined);

  // Build query params (sort + pagination are server-side so they apply to the
  // WHOLE mailbox, not just the loaded page).
  const queryString = useMemo(() => {
    const q = new URLSearchParams();
    q.set("folder", folder);
    q.set("size", String(pageSize));
    q.set("page", String(page));
    q.set("sort", sortField);
    q.set("sort_dir", sortDir);   // 백엔드 파라미터명은 sort_dir (예전 'dir'은 무시돼 항상 desc였음)
    if (searchTrigger) q.set("q", searchTrigger);
    if (senderQuery) q.set("sender", senderQuery);
    if (filter.unread) q.set("unread", "true");
    if (filter.starred && folder !== "STARRED") q.set("starred", "true");
    if (filter.important && folder !== "IMPORTANT") q.set("important", "true");
    if (filter.has_attachments) q.set("has_attachments", "true");
    if (filter.failed) q.set("failed", "true");
    if (filter.label_id) q.set("label_id", String(filter.label_id));
    return q.toString();
  }, [folder, searchTrigger, senderQuery, filter, pageSize, page, sortField, sortDir]);

  const reload = useCallback(async () => {
    setActiveMailbox(mailbox);   // ensure interceptor scope is correct before fetching
    setItems(null);
    try {
      // 목록은 항상 새로 받고(내용이 바뀌므로), 곁다리(숫자·라벨)는 캐시로 묶는다.
      // 사이드바가 같은 것을 부르기 때문에 묶지 않으면 화면당 수십 번 왕복한다.
      const [listRes, countsData, labelsData] = await Promise.all([
        api.get<{ data: Mail[]; meta: { total: number; total_pages: number } }>(`/mail?${queryString}`),
        cachedGet<FolderCounts>("mail:counts", "/mail/counts"),
        cachedGet<MailLabel[]>("mail:labels", "/mail/labels/"),
      ]);
      setItems(listRes.data.data);
      setTotal(listRes.data.meta?.total ?? 0);
      setCounts(countsData);
      setLabels(Array.isArray(labelsData) ? labelsData : []);
      window.dispatchEvent(new Event("mail:refresh"));   // keep the sidebar tree counts fresh
    } catch (e: any) {
      setItems([]);
      toast.error("메일 불러오기 실패", e?.response?.data?.error?.message);
    }
  }, [queryString, mailbox]);

  useEffect(() => {
    void reload();
    setSelectedIds(new Set());
  }, [reload, reloadKey.current]);

  // Sidebar actions (모두 읽음 / 비우기 / 라벨 변경 등) ask the list to refresh.
  useEffect(() => {
    const h = () => { invalidateMailCache(); reloadKey.current++; void reload(); };
    window.addEventListener("mail:reload", h);
    return () => window.removeEventListener("mail:reload", h);
  }, [reload]);

  // Sync the label filter with the URL (?label=) chosen in the sidebar tree.
  useEffect(() => {
    const want = labelParam ? Number(labelParam) : null;
    setFilter((f) => (f.label_id === want ? f : { ...f, label_id: want }));
  }, [labelParam]);

  // Back to page 1 whenever the view (folder / search / filter / sort / size) changes.
  useEffect(() => { setPage(1); }, [folder, searchTrigger, senderQuery, sortField, sortDir, pageSize, filter, labelParam, mailbox]);

  // Moving to another folder/label closes the open mail AND clears search/sender filter.
  useEffect(() => {
    setSel(null); detailPushed.current = false;
    setSearch(""); setSearchTrigger(""); setSenderQuery("");
  }, [folder, labelParam]);

  // 상세보기를 열면 history 항목을 추가 → 브라우저 뒤로가기 시 페이지를 떠나지 않고 상세보기만 닫힘.
  useEffect(() => {
    if (sel && !detailPushed.current) {
      detailPushed.current = true;
      window.history.pushState({ mailDetail: true }, "");
    }
  }, [sel]);
  useEffect(() => {
    const onPop = () => { detailPushed.current = false; setSel(null); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const closeDetail = useCallback(() => {
    setSel(null);
    setPeekList(false);
    if (detailPushed.current) { detailPushed.current = false; window.history.back(); }
  }, []);

  // Load custom folders ("내 메일함") — for the header title + 이동 submenu.
  const loadFolders = useCallback(() => {
    void cachedGet<MailFolder[]>("mail:folders", "/mail/folders/")
      .then((d) => setCustomFolders(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  useEffect(() => { loadFolders(); }, [loadFolders, reloadKey.current]);
  useEffect(() => {
    const h = () => loadFolders();
    window.addEventListener("mail:refresh", h);
    return () => window.removeEventListener("mail:refresh", h);
  }, [loadFolders]);

  // Load the list of mailboxes I can open (내 메일 + 공용 편지함 [+ 관리자=전체])
  useEffect(() => {
    void api.get<{ mailboxes: Mbx[]; can_view_all: boolean }>("/mail/mailboxes")
      .then((r) => { setMailboxes(r.data.mailboxes); setCanViewAll(r.data.can_view_all); })
      .catch(() => {});
    return () => setActiveMailbox(null);   // reset module-level scope when leaving mail
  }, []);

  // "열기" from 공용 편지함 화면 (?mbx=) preselects that mailbox
  useEffect(() => {
    setMailbox(mbxParam ? Number(mbxParam) : null);
  }, [mbxParam]);

  // Switching mailbox: update interceptor scope, clear open mail/selection (it belonged to the old box)
  useEffect(() => {
    setActiveMailbox(mailbox);
    setSel(null);
    setSelectedIds(new Set());
  }, [mailbox]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      // 입력 중이거나(입력칸·본문 편집 영역) 브라우저 조합키면 단축키로 보지 않는다.
      // Ctrl+C(복사)·Ctrl+A 등이 작성창을 여는 문제가 있었다.
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      const combo = e.ctrlKey || e.metaKey || e.altKey;

      if (e.key === "Escape") { setSel(null); return; }
      if (combo) return;                       // 이 아래는 단독 키 단축키만

      if (e.key === "c") { openCompose(); }
      if (e.key === "/") { e.preventDefault(); document.getElementById("mail-q")?.focus(); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close the right-click menu on any click / scroll / Escape
  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setCtx(null); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", esc);
    };
  }, [ctx]);

  // ── Actions ────────────────────────────────────────────────────────────
  const openMail = async (m: Mail) => {
    try {
      const r = await api.get<MailDetail>(`/mail/${m.id}`);
      setSel(r.data);
      setPeekList(false);          // 목록에서 골랐으니 다시 상세를 보여 준다
      if (!m.is_read) {
        setItems((cur) => cur?.map((x) => (x.id === m.id ? { ...x, is_read: true } : x)) ?? null);
        // 화면에 보이는 숫자를 먼저 줄여 즉각 반응하게 한다(낙관적 갱신).
        if (counts && folder === "INBOX") setCounts({ ...counts, unread_inbox: Math.max(0, counts.unread_inbox - 1) });
        // 그리고 사이드바 폴더 수·좌측 레일의 메일 뱃지까지 다시 세게 한다.
        // 이 이벤트를 안 쏘면 뱃지가 폴링 주기(1분)까지 그대로 남아 "안 바뀐다"고 느낀다.
        window.dispatchEvent(new Event("mail:refresh"));
      }
    } catch (e: any) {
      toast.error("열기 실패", e?.response?.data?.error?.message);
    }
  };

  // 알림을 눌러 들어오면(/mail?open=<id>) 그 메일을 바로 연다. 열고 나면 주소에서
  // 떼어 낸다 — 남겨 두면 새로 고칠 때마다 다시 열려 닫을 수가 없다.
  const openParam = params.get("open");
  useEffect(() => {
    const id = Number(openParam);
    if (!openParam || !Number.isFinite(id) || id <= 0) return;
    void openMail({ id, is_read: false } as Mail);
    const next = new URLSearchParams(params);
    next.delete("open");
    const q = next.toString();
    nav({ search: q ? `?${q}` : "" }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam]);

  const toggleSelected = (id: number) => {
    setSelectedIds((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const allChecked = items && items.length > 0 && items.every((m) => selectedIds.has(m.id));
  const toggleAll = () => {
    setSelectedIds(allChecked ? new Set() : new Set(items?.map((m) => m.id) || []));
  };

  const folderLabelOf = (code: string) =>
    customFolders.find((f) => f.code === code)?.name || FOLDER_LABELS[code] || code;

  // 5건 이상을 폴더로 '이동'하면 → 그 발신자들의 메일을 앞으로 자동 분류할지 제안
  const offerAutoClassify = (folder: string | undefined, ids: number[]) => {
    if (!folder || ids.length < 5) return;
    const set = new Set(ids);
    const senders = Array.from(new Set(
      (items ?? []).filter((m) => set.has(m.id)).map((m) => senderEmail(m.from_addr)).filter(Boolean)
    ));
    if (senders.length) setAutoClassify({ folder, senders, count: ids.length });
  };

  const createAutoRules = async () => {
    if (!autoClassify) return;
    const { folder, senders } = autoClassify;
    const label = folderLabelOf(folder);
    let ok = 0;
    let movedTotal = 0;   // 서버가 기존 메일을 몇 통 옮겼는지 합산
    for (const s of senders) {
      try {
        const r = await api.post<{ applied_to_existing?: number }>("/mail/rules/", {
          name: `자동분류: ${s} → ${label}`,
          from_contains: s, move_to_folder: folder, enabled: true, match_all: true,
        });
        ok++;
        movedTotal += r.data?.applied_to_existing ?? 0;
      } catch { /* 중복/오류는 건너뜀 */ }
    }
    // 규칙을 만드는 이유는 대개 이미 쌓인 메일 때문이다. 서버가 기존 메일까지 옮겨 주는데
    // 그 결과를 안 알려 주면 "안 옮겨졌다"고 오해한다. 실제 건수를 함께 보여 준다.
    toast.success(
      `자동 분류 규칙 ${ok}개를 추가했습니다`,
      movedTotal > 0
        ? `기존 메일 ${movedTotal}통도 ${label}(으)로 옮겼습니다`
        : "이후 같은 발신자의 메일이 자동 분류됩니다",
    );
    setAutoClassify(null);
    // 옮겨 놓고 목록을 그대로 두면 화면에는 아직 남아 있는 것처럼 보인다 — 반드시 다시 읽는다.
    invalidateMailCache();
    setSelectedIds(new Set());
    reloadKey.current++;
    void reload();
    window.dispatchEvent(new Event("mail:refresh"));
  };

  const bulk = async (action: string, extra?: Record<string, unknown>) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      await api.post("/mail/bulk", { ids, action, ...extra });
      toast.success(`${ids.length}건 처리됨`);
      setSelectedIds(new Set());
      invalidateMailCache(); reloadKey.current++; void reload();
      if (action === "move") offerAutoClassify(extra?.folder as string | undefined, ids);
    } catch (e: any) {
      toast.error("일괄 처리 실패", e?.response?.data?.error?.message);
    }
  };

  const toggleStar = async (m: Mail, e: React.MouseEvent) => {
    e.stopPropagation();
    setItems((cur) => cur?.map((x) => (x.id === m.id ? { ...x, is_starred: !x.is_starred } : x)) ?? null);
    try {
      await api.patch(`/mail/${m.id}`, { is_starred: !m.is_starred });
    } catch {
      // revert
      setItems((cur) => cur?.map((x) => (x.id === m.id ? { ...x, is_starred: m.is_starred } : x)) ?? null);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post(`/mail/mark-all-read?folder=${folder}`);
      toast.success("모두 읽음 처리");
      invalidateMailCache(); reloadKey.current++; void reload();
    } catch (e: any) {
      toast.error("실패", e?.response?.data?.error?.message);
    }
  };

  const emptyTrash = async () => {
    if (!confirm("휴지통의 모든 메일을 영구 삭제하시겠습니까?")) return;
    try {
      await api.post("/mail/empty-trash");
      toast.success("휴지통 비웠습니다");
      invalidateMailCache(); reloadKey.current++; void reload();
    } catch (e: any) {
      toast.error("실패", e?.response?.data?.error?.message);
    }
  };

  const onPickEml = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setImporting(true);
    try {
      // No timeout — large/many .eml uploads can take a while (default client is 30s).
      // 현재 보고 있는 폴더로 가져온다(보낸메일함이면 보낸 메일로 저장).
      const r = await api.post<{ imported: number; skipped?: number; failed: string[] }>(`/mail/import/eml?folder=${encodeURIComponent(folder)}`, fd, { timeout: 0 });
      const { imported, failed } = r.data;
      const skipped = r.data.skipped ?? 0;
      // 이미 있는 메일은 실패가 아니라 건너뛴 것 — 같은 폴더를 다시 올리는 일이 흔하다.
      const extra = [
        skipped ? `${skipped}개는 이미 있어 건너뜀` : "",
        failed.length ? `${failed.length}개 실패` : "",
      ].filter(Boolean).join(" · ");
      if (imported > 0) {
        toast.success(`${imported}개 메일을 가져왔습니다`, extra || undefined);
      } else if (skipped > 0 && !failed.length) {
        toast.success("새로 가져올 메일이 없습니다", `${skipped}개 모두 이미 있는 메일입니다`);
      } else {
        toast.error("가져오기 실패", failed.length ? `${failed.length}개 파일을 처리하지 못했습니다` : "유효한 eml 파일이 없습니다");
      }
      invalidateMailCache(); reloadKey.current++; void reload();
    } catch (err: any) {
      toast.error("가져오기 실패", err?.response?.data?.error?.message);
    } finally {
      setImporting(false);
      if (emlInputRef.current) emlInputRef.current.value = "";   // allow re-picking the same file
    }
  };

  const onReply = (m: MailDetail) => {
    openCompose({
      to_addrs: [m.from_addr],
      subject: m.subject?.startsWith("Re:") ? m.subject : `Re: ${m.subject || ""}`,
      body_text: quoteOriginal(m, "reply"),
      in_reply_to_id: m.id,
    });
  };

  // 전달 — 본문만 넘기고 첨부를 빠뜨리던 것을 고쳤다. 서버가 첨부를 새 파일로
  // 복사해 주면 그걸 달고 작성 화면을 연다. 원본 첨부 id 를 그대로 쓰면 발송 시
  // 첨부가 원본 메일에서 떨어져 나가므로(이동이지 복사가 아니다) 반드시 이 경로로 간다.
  const onForward = async (m: MailDetail) => {
    const base = {
      to_addrs: [],
      subject: m.subject?.startsWith("Fwd:") ? m.subject : `Fwd: ${m.subject || ""}`,
      body_text: quoteOriginal(m, "forward"),
    };
    if (!m.has_attachments) { openCompose(base); return; }
    try {
      const r = await api.post(`/mail/${m.id}/forward-attachments`, {});
      openCompose({ ...base, attachments: r.data });
    } catch {
      // 첨부 복사에 실패해도 전달 자체는 막지 않는다 — 다만 조용히 빠뜨리지는 않는다.
      toast.warn("첨부를 가져오지 못했습니다", "본문만 전달 창을 엽니다. 필요하면 직접 첨부해 주세요.");
      openCompose(base);
    }
  };

  const onReplyAll = (m: MailDetail) => {
    const cc = Array.from(new Set([...m.to_addrs, ...m.cc_addrs]))
      .filter((a) => a && a !== m.from_addr && a !== myAddr);
    openCompose({
      to_addrs: [m.from_addr],
      cc_addrs: cc,
      subject: m.subject?.startsWith("Re:") ? m.subject : `Re: ${m.subject || ""}`,
      body_text: quoteOriginal(m, "reply"),
      in_reply_to_id: m.id,
    });
  };

  // 본문·헤더의 메일 주소를 눌렀을 때: 그 사람 앞으로 새 메일을 연다.
  // "이름 <a@b>" 형태로 와도 주소만 뽑아 넣는다.
  const onComposeTo = (raw: string) => {
    const addr = (raw.match(/<([^>]+)>/)?.[1] || raw).trim();
    if (!addr.includes("@")) { toast.warn("메일 주소가 아닙니다"); return; }
    openCompose({ to_addrs: [addr] });
  };

  // 다시 보내기 — 서버가 첨부를 새 파일로 복사해 주면 그걸 달고 작성 화면을 연다.
  // (원본 첨부를 그대로 쓰면 새 메일에서 지웠을 때 보낸메일함 원본까지 사라진다)
  const onResend = async (m: MailDetail) => {
    try {
      const r = await api.post(`/mail/${m.id}/resend`, {});
      openCompose({
        to_addrs: r.data.to_addrs,
        cc_addrs: r.data.cc_addrs,
        bcc_addrs: r.data.bcc_addrs,
        subject: r.data.subject,
        body_text: r.data.body_text,
        body_html: r.data.body_html,
        attachments: r.data.attachments,
      });
    } catch {
      toast.error("다시 보내기를 준비하지 못했습니다");
    }
  };

  const searchSender = (m: Mail) => {
    const email = senderEmail(m.from_addr);
    setSel(null);
    setSearch(""); setSearchTrigger("");   // 일반 검색과 분리
    setSenderQuery(email);                 // 발신자 정확 일치(서버측, 본문/제목 매칭 안 함)
  };

  const deleteSender = async (m: Mail) => {
    const email = senderEmail(m.from_addr);
    let cnt = 0;
    try {
      const c = await api.get<{ count: number }>(`/mail/by-sender/count?sender=${encodeURIComponent(email)}`);
      cnt = c.data.count;
    } catch { /* count 실패 시에도 진행 가능하게 */ }
    if (cnt === 0) { toast.warn("해당 발신자가 보낸 메일이 없습니다", email); return; }
    if (!confirm(`'${email}' 님이 보낸 메일 ${cnt}건을 모두 휴지통으로 이동할까요?`)) return;
    try {
      const r = await api.post<{ affected: number }>("/mail/by-sender/delete", { sender: email });
      toast.success(`${r.data.affected}건을 휴지통으로 이동했습니다`);
      setSel(null); setSelectedIds(new Set());
      invalidateMailCache(); reloadKey.current++; void reload();
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.error?.message);
    }
  };

  // 발신자 기준 자동 분류 규칙 생성 — 앞으로 이 사람이 보낸 메일을 특정 메일함으로 자동 이동.
  const autoFileSender = async (m: Mail, folderCode: string, folderLabel: string) => {
    const email = senderEmail(m.from_addr);
    try {
      const r = await api.post<{ applied_to_existing?: number }>("/mail/rules/", {
        name: `${email} → ${folderLabel}`,
        from_contains: email,
        move_to_folder: folderCode,
        match_all: true,
      });
      // 규칙을 만드는 이유는 대개 이미 쌓인 메일 때문 — 서버가 기존 메일까지 옮겨 준다.
      const moved = r.data.applied_to_existing ?? 0;
      toast.success(
        moved > 0 ? `${moved}개 메일을 '${folderLabel}'로 옮겼습니다` : "자동 분류 추가됨",
        `앞으로 ${email} 님의 메일은 '${folderLabel}'에 자동 저장됩니다`,
      );
      setSel(null); setSelectedIds(new Set());
      invalidateMailCache(); reloadKey.current++; void reload();
      window.dispatchEvent(new Event("mail:refresh"));
    } catch (e: any) {
      toast.error("자동 분류 추가 실패", e?.response?.data?.error?.message || e?.response?.data?.detail);
    }
  };

  const exportMail = async () => {
    try {
      const ids = selectedIds.size > 0 ? `&ids=${Array.from(selectedIds).join(",")}` : "";
      const r = await api.get(`/mail/export?folder=${encodeURIComponent(folder)}${ids}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timbox-mail-${folder}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(selectedIds.size > 0 ? `${selectedIds.size}건 내보냈습니다` : "현재 메일함을 내보냈습니다");
    } catch (e: any) {
      toast.error("내보내기 실패", e?.response?.data?.error?.message);
    }
  };

  // ── Context-menu helpers ───────────────────────────────────────────────
  // Flag/move/spam/delete act on the whole selection if the clicked row is part
  // of it; reply/forward/open/print/label act on the single clicked mail.
  const ctxTargets = (m: Mail): number[] =>
    selectedIds.has(m.id) && selectedIds.size > 0 ? Array.from(selectedIds) : [m.id];

  const runBulk = async (m: Mail, action: string, extra?: Record<string, unknown>) => {
    const ids = ctxTargets(m);
    try {
      await api.post("/mail/bulk", { ids, action, ...extra });
      setSelectedIds(new Set());
      invalidateMailCache(); reloadKey.current++; void reload();
      if (sel && ids.includes(sel.id)) setSel(null);
      if (action === "move") offerAutoClassify(extra?.folder as string | undefined, ids);
    } catch (e: any) { toast.error("처리 실패", e?.response?.data?.error?.message); }
  };

  const withDetail = async (m: Mail, fn: (d: MailDetail) => void) => {
    try { const r = await api.get<MailDetail>(`/mail/${m.id}`); fn(r.data); }
    catch (e: any) { toast.error("열기 실패", e?.response?.data?.error?.message); }
  };

  const toggleLabelOn = async (m: Mail, labelId: number) => {
    const has = m.labels.some((l) => l.id === labelId);
    const next = has
      ? m.labels.filter((l) => l.id !== labelId).map((l) => l.id)
      : [...m.labels.map((l) => l.id), labelId];
    try {
      await api.post(`/mail/${m.id}/labels`, { label_ids: next });
      invalidateMailCache(); reloadKey.current++; void reload();
      if (sel?.id === m.id) void api.get<MailDetail>(`/mail/${m.id}`).then((r) => setSel(r.data)).catch(() => {});
    } catch (e: any) { toast.error("라벨 변경 실패", e?.response?.data?.error?.message); }
  };

  const openMailWindow = (m: MailDetail, doPrint: boolean) => {
    const w = window.open("", "_blank", "width=780,height=860");
    if (!w) { toast.warn("팝업이 차단되었습니다", "브라우저의 팝업 차단을 해제하세요"); return; }
    w.document.write(buildMailDoc(m));
    w.document.close();
    w.focus();
    if (doPrint) setTimeout(() => { try { w.print(); } catch { /* user cancelled */ } }, 350);
  };

  // Hover quick-action on a single row (read / important / spam / delete).
  const rowQuick = (_m: Mail, e: React.MouseEvent, run: () => Promise<unknown>) => {
    e.stopPropagation();
    run().then(() => { invalidateMailCache(); reloadKey.current++; void reload(); })
      .catch((er: any) => toast.error("처리 실패", er?.response?.data?.error?.message));
  };

  // 주소 → 이름 사전(조직도 + 주소록). 목록에서 주소 대신 이름을 보여 주는 데 쓴다.
  const { namesOf } = useAddressNames();

  // The backend already sorts the whole mailbox + paginates; show as returned.
  const displayItems = items ?? [];

  const onEditDraft = async (m: Mail) => {
    try {
      const r = await api.get<MailDetail>(`/mail/${m.id}`);
      openCompose({ ...r.data, draftId: m.id });
    } catch (e: any) {
      toast.error("열기 실패", e?.response?.data?.error?.message);
    }
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTrigger(search.trim());
  };

  const customFolder = customFolders.find((f) => f.code === folder);
  const folderTitle = customFolder ? customFolder.name : (FOLDER_LABELS[folder] || folder);
  const folderSubtitle = (() => {
    if (customFolder) return `전체 ${customFolder.total}건`;
    if (!counts) return "";
    if (folder === "INBOX") return `안 읽음 ${counts.unread_inbox}건 · 전체 ${counts.INBOX}건`;
    const key = folder as keyof FolderCounts;
    const c = counts[key];
    return typeof c === "number" ? `전체 ${c}건` : "";
  })();

  // External account / shared mailbox / settings views
  if (folder === "ACCOUNTS") return <MailAccountsView />;
  if (folder === "SHARED") return <SharedMailboxesView />;
  if (folder === "CONTACTS") return <ContactsView />;
  if (folder === "SETTINGS")
    return <MailSettings asPage open onClose={() => nav("/mail")}
      onChange={() => { window.dispatchEvent(new Event("mail:refresh")); }} />;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="mail-wrap">
      <div className="t-page-header" style={{ padding: "20px 24px 4px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1>{folderTitle}</h1>
            {(mailboxes.length > 1 || canViewAll) && (
              <select
                className="mail-filter-chip"
                style={{ height: 28, paddingRight: 24, fontWeight: 600 }}
                value={mailbox ?? ""}
                onChange={(e) => setMailbox(e.target.value ? Number(e.target.value) : null)}
                title="편지함 전환"
              >
                {mailboxes.map((mb) => (
                  <option key={mb.user_id} value={mb.kind === "self" ? "" : mb.user_id}>
                    {mb.kind === "self" ? "📥 내 메일함" : (mb.kind === "shared" ? `👥 ${mb.label}` : `👤 ${mb.label}`)}
                  </option>
                ))}
                {mailbox != null && !mailboxes.some((x) => x.user_id === mailbox) && (
                  <option value={mailbox}>{activeMbx?.label || "선택한 메일함"}</option>
                )}
              </select>
            )}
            {isSuperAdmin && canViewAll && (
              <button className="t-btn t-btn-sm" onClick={() => setOrgOpen(true)} title="다른 사용자 메일함 열람 (최고관리자)">
                👤 사용자 메일함
              </button>
            )}
          </div>
          <div className="subtitle">
            {activeMbx
              ? <span style={{ color: "var(--p-600,#4f46e5)" }}>
                  {activeMbx.kind === "shared" ? "공용 편지함" : "열람"} · {activeMbx.alias || activeMbx.label} — {folderSubtitle}
                </span>
              : folderSubtitle}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="t-btn t-btn-primary" onClick={() => openCompose()}>
            <I.Plus size={14}/> 메일 작성
          </button>
          <input
            ref={emlInputRef}
            type="file"
            accept=".eml,message/rfc822"
            multiple
            style={{ display: "none" }}
            onChange={onPickEml}
          />
          <button
            className="t-btn"
            onClick={() => emlInputRef.current?.click()}
            disabled={importing}
            title={`외부 eml 파일을 ${folderLabelOf(folder)}(으)로 일괄 가져오기`}
          >
            {importing ? "가져오는 중…" : <><I.Paper size={14}/> eml 가져오기</>}
          </button>
          <button className="t-btn" onClick={() => setSettingsOpen(true)} title="라벨/서명 설정">
            <I.Settings size={14}/>
          </button>
        </div>
      </div>
      {orgOpen && (
        <OrgPicker
          title="메일함을 열람할 사용자 선택"
          multi={false}
          onPick={(us) => { if (us[0]) pickUserMailbox(us[0]); }}
          onClose={() => setOrgOpen(false)}
        />
      )}

      {/* Toolbar */}
      <div className="mail-toolbar">
        <form onSubmit={submitSearch} style={{ flex: 1, display: "flex", minWidth: 240, position: "relative" }}>
          <input
            id="mail-q"
            className="mail-search"
            placeholder="메일 검색 (발신자, 제목, 본문)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onBlur={() => setSearchTrigger(search.trim())}
            style={{ flex: 1, paddingRight: 30 }}
          />
          {(search || searchTrigger || senderQuery) && (
            <button type="button" className="mail-search-clear" title="검색 지우기"
              onClick={() => { setSearch(""); setSearchTrigger(""); setSenderQuery(""); }}>
              <I.X size={14}/>
            </button>
          )}
        </form>

        <FilterChip active={filter.unread} onClick={() => setFilter({ ...filter, unread: !filter.unread })}>
          안 읽음
        </FilterChip>
        <FilterChip active={filter.starred} onClick={() => setFilter({ ...filter, starred: !filter.starred })}>
          <I.Star size={11}/> 별표
        </FilterChip>
        <FilterChip active={filter.important} onClick={() => setFilter({ ...filter, important: !filter.important })}>
          중요
        </FilterChip>
        <FilterChip active={filter.has_attachments} onClick={() => setFilter({ ...filter, has_attachments: !filter.has_attachments })}>
          <I.Paper size={11}/> 첨부
        </FilterChip>
        {/* 발송 실패는 흩어져 있으면 놓친다 — 한 번에 모아 볼 수 있게 한다. */}
        <FilterChip active={filter.failed} onClick={() => setFilter({ ...filter, failed: !filter.failed })}>
          ⚠️ 발송실패
        </FilterChip>

        {labels.length > 0 && (
          <select
            className="mail-filter-chip"
            style={{ height: 26, paddingRight: 24 }}
            value={filter.label_id ?? ""}
            onChange={(e) => setFilter({ ...filter, label_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">모든 라벨</option>
            {labels.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        )}

        <div className="spacer"/>

        <button className={`t-btn t-btn-sm ${previewOn ? "t-btn-primary" : ""}`}
          title={previewOn ? "본문 미리보기 끄기" : "본문 미리보기 켜기 (목록에 한 줄, 마우스를 올리면 더 보기)"}
          onClick={() => { const v = !previewOn; setPreviewOn(v); localStorage.setItem("mail-preview", v ? "on" : "off"); clearPeek(); }}>
          미리보기
        </button>

        <button className="t-btn t-btn-sm" onClick={() => { invalidateMailCache(); reloadKey.current++; void reload(); }}>
          새로고침
        </button>
        <button className="t-btn t-btn-sm" onClick={exportMail} title={selectedIds.size > 0 ? "선택 메일 내보내기" : "현재 메일함 내보내기"}>
          ⬇ 내보내기
        </button>
        {(["INBOX", "RECEIVED", "ALL", "ADS", "SPAM", "STARRED", "IMPORTANT"].includes(folder) || folder.startsWith("CF")) && (
          <button className="t-btn t-btn-sm" onClick={markAllRead}>
            모두 읽음
          </button>
        )}
        {folder === "TRASH" && (
          <button className="t-btn t-btn-sm t-btn-danger" onClick={emptyTrash}>
            <I.Trash size={12}/> 휴지통 비우기
          </button>
        )}
      </div>

      {/* 발신자 필터 표시 */}
      {senderQuery && (
        <div className="mail-bulkbar">
          <span>📧 <strong>{senderQuery}</strong> 님이 보낸 메일만 표시 중</span>
          <button className="t-btn t-btn-sm" style={{ marginLeft: "auto" }} onClick={() => setSenderQuery("")}>필터 해제 ✕</button>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mail-bulkbar">
          <strong>{selectedIds.size}개 선택됨</strong>
          <div className="bulk-actions">
            <button className="t-btn t-btn-sm" onClick={() => bulk("read")}>읽음</button>
            <button className="t-btn t-btn-sm" onClick={() => bulk("unread")}>안 읽음</button>
            <button className="t-btn t-btn-sm" onClick={() => bulk("star")}>별표</button>
            <button className="t-btn t-btn-sm" onClick={() => bulk("unstar")}>별표 해제</button>
            <select
              className="mail-filter-chip"
              style={{ height: 28, paddingRight: 22 }}
              value=""
              onChange={(e) => { const v = e.target.value; if (v) bulk("move", { folder: v }); e.currentTarget.value = ""; }}
              title="선택한 메일을 메일함으로 이동"
            >
              <option value="">📁 메일함 이동…</option>
              <option value="INBOX">받은 메일함</option>
              <option value="ADS">광고 메일함</option>
              <option value="SPAM">스팸함</option>
              <option value="TRASH">휴지통</option>
              {customFolders.length > 0 && (
                <optgroup label="내 메일함">
                  {flattenFolders(customFolders).map(({ folder: f, depth }) => (
                    <option key={f.code} value={f.code}>
                      {`${"  ".repeat(depth)}${depth > 0 ? "└ " : ""}${f.name}`}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button className="t-btn t-btn-sm t-btn-danger" onClick={() => bulk("trash")}>휴지통</button>
            <button className="t-btn t-btn-sm" onClick={() => setSelectedIds(new Set())}>취소</button>
          </div>
        </div>
      )}

      {/* 컬럼 헤더(구분) — 제목 줄. 클릭하면 그 기준으로 정렬 */}
      {items && items.length > 0 && (
        <div className="mail-col-head">
          <div className={`check ${allChecked ? "checked" : ""}`} onClick={toggleAll} title="전체 선택">
            {allChecked && <I.Check size={12} style={{ color: "white" }}/>}
          </div>
          <span/>
          <span/>
          <ColHead label={folder === "SENT" || folder === "DRAFT" ? "받는사람" : "발신자"} field="from" sortField={sortField} sortDir={sortDir} onSort={onSortTab}/>
          <ColHead label="제목" field="subject" sortField={sortField} sortDir={sortDir} onSort={onSortTab}/>
          <ColHead label="용량" field="size" num sortField={sortField} sortDir={sortDir} onSort={onSortTab}/>
          <ColHead label="날짜" field="date" num sortField={sortField} sortDir={sortDir} onSort={onSortTab}/>
        </div>
      )}

      {/* List */}
      <div className="mail-list">
        {items === null && <Loading/>}
        {items?.length === 0 && (
          <Empty msg={
            searchTrigger ? "검색 결과가 없습니다"
              : folder === "TRASH" ? "휴지통이 비어 있습니다"
              : folder === "DRAFT" ? "임시 저장된 메일이 없습니다"
              : "메일이 없습니다"
          }/>
        )}
        {peek && <MailPeek peek={peek} isOut={folder === "SENT" || folder === "DRAFT"} namesOf={namesOf} senderName={senderName}/>}
      {items && items.length > 0 && displayItems.map((m) => {
          const isOut = folder === "SENT" || folder === "DRAFT" || m.folder === "DRAFT";
          // 보낸메일함은 주소 대신 사람 이름으로 보여 준다(주소는 툴팁에 남긴다).
          // to_addrs 에는 이름이 없으므로 조직도·주소록에서 찾아 붙인다.
          const who = isOut
            ? `→ ${namesOf(m.to_addrs) || "(받는 사람 없음)"}`
            : senderName(m.from_addr);
          return (
            <div
              key={m.id}
              className={`mail-row ${!m.is_read ? "unread" : ""} ${sel?.id === m.id ? "selected" : ""} ${ctx?.mail.id === m.id ? "ctx-active" : ""} ${m.folder === "SCHEDULED" ? "scheduled" : ""}`}
              onClick={() => { clearPeek(); return folder === "DRAFT" || m.folder === "DRAFT" ? onEditDraft(m) : openMail(m); }}
              onMouseEnter={(e) => startPeek(m, e)}
              onMouseLeave={clearPeek}
              onContextMenu={(e) => { e.preventDefault(); clearPeek(); setCtx({ x: e.clientX, y: e.clientY, mail: m }); }}
            >
              <div className={`check ${selectedIds.has(m.id) ? "checked" : ""}`}
                   onClick={(e) => { e.stopPropagation(); toggleSelected(m.id); }}>
                {selectedIds.has(m.id) && <I.Check size={12}/>}
              </div>

              <button className={`star ${m.is_starred ? "on" : ""}`}
                      onClick={(e) => toggleStar(m, e)} title="별표"
                      aria-label={m.is_starred ? "별표 해제" : "별표 추가"}>
                {m.is_starred ? <I.StarF size={15}/> : <I.Star size={15}/>}
              </button>

              <div className="avatar" style={{ background: colorOf(m.from_addr) }}>
                {initials(senderName(m.from_addr))}
              </div>

              {/* 이름으로 보여 주되, 실제 주소는 툴팁으로 확인할 수 있게 남긴다. */}
              <span className="from" title={isOut ? m.to_addrs.join(", ") : m.from_addr}>{who}</span>

              <span className="subject-line">
                <span className="subject">{m.subject || "(제목 없음)"}</span>
                {m.labels.map((l) => (
                  <span key={l.id} className="label-chip"
                        style={{ background: `color-mix(in srgb, ${l.color} 16%, transparent)`, color: l.color, marginLeft: 4, flexShrink: 0 }}>
                    {l.name}
                  </span>
                ))}
                {m.has_attachments && <I.Paper size={13} className="icon-attach"/>}
                {/* 발송 결과를 목록에서 바로 보여 준다.
                    실패만 표시하면 "표시 없음"이 성공인지 처리 중인지 구분되지 않아,
                    보낸 사람이 도달 여부를 확신할 수 없다. 그래서 성공도 함께 찍는다. */}
                {isOut && m.folder === "SENT" && (
                  m.delivery_error ? (
                    <span title={m.delivery_error}
                      style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
                               background: "rgba(239,68,68,.12)", color: "var(--danger,#ef4444)",
                               border: "1px solid rgba(239,68,68,.35)", whiteSpace: "nowrap" }}>발송실패</span>
                  ) : (
                    <span title="메일 서버가 정상적으로 접수했습니다"
                      style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
                               background: "rgba(34,197,94,.12)", color: "var(--success,#16a34a)",
                               border: "1px solid rgba(34,197,94,.30)", whiteSpace: "nowrap" }}>발송완료</span>
                  )
                )}
                  {isOut && m.folder === "SENT" && receiptSum[m.id] && receiptSum[m.id].total > 0 && (
                    <span title="수신 확인: 읽음·열람한 사람 수 / 수신자 수 (자세한 내용은 메일을 열면 보입니다)"
                      style={{ fontSize: 10.5, fontWeight: 600, padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap",
                               background: receiptSum[m.id].read > 0 ? "rgba(59,130,246,.12)" : "transparent",
                               color: receiptSum[m.id].read > 0 ? "#2563eb" : "var(--text-3)", border: "1px solid var(--border)" }}>
                      읽음 {receiptSum[m.id].read}/{receiptSum[m.id].total}
                    </span>
                  )}
                {m.is_important && <I.AlertC size={13} style={{ color: "var(--danger)", flexShrink: 0 }}/>}
                {previewOn && m.preview && <span className="preview">{m.preview}</span>}
              </span>

              <span className="mail-size">{m.size_bytes > 0 ? formatBytes(m.size_bytes) : ""}</span>

              <span className="when">
                {m.scheduled_at ? `예약: ${fmtDate(m.scheduled_at)}` : fmtDate(m.received_at || m.sent_at || m.created_at)}
              </span>

              <div className="row-actions" onClick={(e) => e.stopPropagation()}>
                <button title={m.is_read ? "안 읽음으로" : "읽음으로"}
                        onClick={(e) => rowQuick(m, e, () => api.patch(`/mail/${m.id}`, { is_read: !m.is_read }))}>
                  <I.Mail size={14}/>
                </button>
                <button title={m.is_important ? "중요 해제" : "중요 표시"}
                        onClick={(e) => rowQuick(m, e, () => api.patch(`/mail/${m.id}`, { is_important: !m.is_important }))}>
                  <I.AlertC size={14} style={{ color: m.is_important ? "var(--danger)" : undefined }}/>
                </button>
                <button title={m.folder === "SPAM" ? "스팸 아님" : "스팸신고"}
                        onClick={(e) => rowQuick(m, e, () => api.patch(`/mail/${m.id}`, { folder: m.folder === "SPAM" ? "INBOX" : "SPAM" }))}>
                  <span style={{ fontSize: 13, lineHeight: 1 }}>🚫</span>
                </button>
                <button title={m.folder === "TRASH" ? "영구 삭제" : "삭제"}
                        onClick={(e) => rowQuick(m, e, () => api.delete(`/mail/${m.id}`))}>
                  <I.Trash size={14}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* 페이지네이션 + 페이지당 개수 */}
      {items && total > 0 && (() => {
        const pages = Math.max(1, Math.ceil(total / pageSize));
        const from = (page - 1) * pageSize + 1;
        const to = Math.min(total, page * pageSize);
        const win = 2;
        const start = Math.max(1, page - win);
        const end = Math.min(pages, page + win);
        const nums: number[] = [];
        for (let i = start; i <= end; i++) nums.push(i);
        return (
          <div className="mail-pagination">
            <span className="info">총 {total.toLocaleString()}건 · {from.toLocaleString()}–{to.toLocaleString()}</span>
            <div className="pg">
              <button disabled={page <= 1} onClick={() => setPage(1)} title="처음">«</button>
              <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="이전">‹</button>
              {start > 1 && (
                <>
                  <button onClick={() => setPage(1)}>1</button>
                  {start > 2 && <span className="dots">…</span>}
                </>
              )}
              {nums.map((n) => (
                <button key={n} className={n === page ? "on" : ""} onClick={() => setPage(n)}>{n}</button>
              ))}
              {end < pages && (
                <>
                  {end < pages - 1 && <span className="dots">…</span>}
                  <button onClick={() => setPage(pages)}>{pages}</button>
                </>
              )}
              <button disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} title="다음">›</button>
              <button disabled={page >= pages} onClick={() => setPage(pages)} title="마지막">»</button>
            </div>
            <select
              className="mail-filter-chip"
              style={{ height: 28, paddingRight: 22 }}
              value={pageSize}
              onChange={(e) => { const n = Number(e.target.value); setPageSize(n); localStorage.setItem("mail-page-size", String(n)); }}
              title="페이지당 메일 수"
            >
              <option value={50}>50개씩</option>
              <option value={100}>100개씩</option>
              <option value={200}>200개씩</option>
            </select>
          </div>
        );
      })()}

      {/* 모바일 전용 — 읽던 메일을 닫지 않고 목록만 잠깐 들춰 본다.
          상세가 목록을 덮는 구조라, 이게 없으면 다른 메일로 넘어갈 때마다 닫아야 한다. */}
      {sel && (
        <button
          className="mail-list-peek"
          onClick={() => setPeekList((v) => !v)}
          title={peekList ? "읽던 메일로 돌아가기" : "메일 목록 보기"}
        >
          {peekList ? "← 읽던 메일" : "☰ 목록"}
        </button>
      )}

      {/* Detail pane */}
      {sel && (
        <MailDetailPane
          className={peekList ? "peek-hidden" : undefined}
          mail={sel}
          labels={labels}
          onClose={closeDetail}
          onChange={() => { invalidateMailCache(); reloadKey.current++; void reload(); if (sel) void api.get<MailDetail>(`/mail/${sel.id}`).then((r) => setSel(r.data)).catch(() => {}); }}
          onReply={onReply}
          onForward={onForward}
          onReplyAll={onReplyAll}
          onResend={onResend}
          onEditDraft={(m) => void onEditDraft(m)}
          onComposeTo={onComposeTo}
          onSearchSender={(addr) => { setSel(null); setSearch(""); setSearchTrigger(""); setSenderQuery(addr); }}
        />
      )}

      {/* Right-click context menu */}
      {ctx && (
        <MailCtxMenu
          x={ctx.x} y={ctx.y} mail={ctx.mail} folder={folder} labels={labels} customFolders={customFolders}
          targetCount={selectedIds.has(ctx.mail.id) ? selectedIds.size : 1}
          onClose={() => setCtx(null)}
          onOpen={(m) => (folder === "DRAFT" || m.folder === "DRAFT" ? onEditDraft(m) : openMail(m))}
          onNewWindow={(m) => withDetail(m, (d) => openMailWindow(d, false))}
          onPrint={(m) => withDetail(m, (d) => openMailWindow(d, true))}
          onReply={(m) => withDetail(m, onReply)}
          onReplyAll={(m) => withDetail(m, onReplyAll)}
          onForward={(m) => withDetail(m, onForward)}
          onResend={(m) => withDetail(m, onResend)}
          onFlag={(m, action) => runBulk(m, action)}
          onMove={(m, f) => runBulk(m, "move", { folder: f })}
          onToggleLabel={toggleLabelOn}
          onDelete={(m) => runBulk(m, "delete")}
          onSearchSender={searchSender}
          onDeleteSender={deleteSender}
          onAutoFile={autoFileSender}
        />
      )}

      {/* 자동 분류 제안 (5건 이상 이동 시) */}
      {autoClassify && (() => {
        const label = folderLabelOf(autoClassify.folder);
        const shown = autoClassify.senders.slice(0, 8);
        return (
          <div className="t-overlay" onClick={() => setAutoClassify(null)}>
            <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: "94vw", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0, flex: 1, fontSize: 15, fontWeight: 700 }}>자동 분류 추가</h3>
                <button className="t-iconbtn" onClick={() => setAutoClassify(null)}><I.X size={16}/></button>
              </div>
              <div style={{ padding: 18, overflow: "auto", fontSize: 13.5, lineHeight: 1.6 }}>
                <p style={{ marginTop: 0 }}>
                  {autoClassify.count}개 메일을 <b>'{label}'</b>(으)로 이동했습니다.<br/>
                  앞으로 아래 발신자가 보낸 메일을 자동으로 <b>'{label}'</b>(으)로 분류할까요?
                </p>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "var(--text-2)" }}>
                  {shown.map((s) => <li key={s}>{s}</li>)}
                  {autoClassify.senders.length > shown.length && (
                    <li style={{ listStyle: "none", color: "var(--text-3)" }}>외 {autoClassify.senders.length - shown.length}명…</li>
                  )}
                </ul>
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-3)" }}>
                  규칙은 설정 → 필터에서 언제든 수정·삭제할 수 있습니다.
                </div>
              </div>
              <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button className="t-btn" onClick={() => setAutoClassify(null)}>아니요</button>
                <button className="t-btn t-btn-primary" onClick={createAutoRules}>자동 분류 규칙 추가</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Settings */}
      <MailSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onChange={() => { invalidateMailCache(); reloadKey.current++; void reload(); }}
      />
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button className={`mail-filter-chip ${active ? "active" : ""}`} onClick={onClick}>
      {children}
    </button>
  );
}

// ── Right-click context menu (Naver-style) ────────────────────────────────
function MailCtxMenu({
  x, y, mail, folder, labels, customFolders, targetCount,
  onClose, onOpen, onNewWindow, onPrint, onReply, onReplyAll, onForward, onResend,
  onFlag, onMove, onToggleLabel, onDelete, onSearchSender, onDeleteSender, onAutoFile,
}: {
  x: number; y: number; mail: Mail; folder: string; labels: MailLabel[]; customFolders: MailFolder[]; targetCount: number;
  onSearchSender: (m: Mail) => void; onDeleteSender: (m: Mail) => void;
  onAutoFile: (m: Mail, folderCode: string, folderLabel: string) => void;
  onClose: () => void;
  onOpen: (m: Mail) => void; onNewWindow: (m: Mail) => void; onPrint: (m: Mail) => void;
  onReply: (m: Mail) => void; onReplyAll: (m: Mail) => void; onForward: (m: Mail) => void;
  onResend: (m: Mail) => void;
  onFlag: (m: Mail, action: string) => void; onMove: (m: Mail, folder: string) => void;
  onToggleLabel: (m: Mail, labelId: number) => void; onDelete: (m: Mail) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  const [sub, setSub] = useState<null | "move" | "label" | "autofile">(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    let nx = x, ny = y;
    if (x + r.width > window.innerWidth - 8) nx = Math.max(8, window.innerWidth - r.width - 8);
    if (y + r.height > window.innerHeight - 8) ny = Math.max(8, window.innerHeight - r.height - 8);
    setPos({ x: nx, y: ny });
  }, [x, y]);

  const multi = targetCount > 1;
  const isSpam = folder === "SPAM";
  const run = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); onClose(); };

  const itemBase: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "7px 10px",
    border: "none", background: "transparent", textAlign: "left", borderRadius: 6, fontSize: 13, cursor: "pointer",
  };
  // indent: 하위 메일함을 한 단계씩 들여쓴다(0 이면 최상위).
  const Item = ({ icon, label, onClick, danger, indent = 0 }: {
    icon: string; label: string; onClick: (e: React.MouseEvent) => void;
    danger?: boolean; indent?: number;
  }) => (
    <button style={{ ...itemBase, color: danger ? "var(--danger,#ef4444)" : "var(--text-1)",
                     paddingLeft: 11 + indent * 14 }} onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--panel)"; }}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      <span style={{ width: 18, textAlign: "center", flexShrink: 0,
                     color: indent > 0 ? "var(--text-3)" : undefined }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
  const Sep = () => <div style={{ height: 1, background: "var(--border)", margin: "4px 6px" }} />;
  // The flyout is a DOM child of the wrapper, so hovering it does NOT trigger the
  // wrapper's onMouseLeave — the submenu stays open until you leave the whole row.
  const SubRow = ({ icon, label, name, children }: { icon: string; label: string; name: "move" | "label" | "autofile"; children: React.ReactNode }) => (
    <div style={{ position: "relative" }} onMouseEnter={() => setSub(name)} onMouseLeave={() => setSub(null)}>
      <button style={itemBase} onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
        <span style={{ width: 18, textAlign: "center", flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ color: "var(--text-3)" }}>▸</span>
      </button>
      {sub === name && (
        <div onMouseEnter={() => setSub(name)}
          style={{ position: "absolute", left: "100%", top: -5, minWidth: 168, background: "var(--surface,#fff)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,.18)", padding: 4, maxHeight: 320, overflowY: "auto" }}>
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
      style={{ position: "fixed", left: pos.x, top: pos.y, zIndex: 1000, minWidth: 204,
        background: "var(--surface,#fff)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 10px 36px rgba(0,0,0,.20)", padding: 4 }}>
      <div style={{ padding: "7px 11px 8px", borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{senderName(mail.from_addr)}</div>
        <div style={{ fontSize: 11, color: "var(--text-3)", userSelect: "text", wordBreak: "break-all" }}>{mail.from_addr}</div>
      </div>
      <Item icon="🔍" label="이 사람이 보낸 메일 검색" onClick={run(() => onSearchSender(mail))} />
      <SubRow icon="📥" label="이 사람 메일 자동 분류" name="autofile">
        {/* 사이드바와 같은 계층·순서로 보여 준다. 평면으로 나열하면 부모와 자식이
            뒤섞여(예: 프로젝트 다음에 그 자식인 서대문구가 아니라 내부 운영이) 헷갈린다. */}
        {flattenFolders(customFolders).map(({ folder: f, depth }) => (
          <Item key={f.code} icon={depth > 0 ? "└" : "📂"} indent={depth}
                label={f.name} onClick={run(() => onAutoFile(mail, f.code, f.name))} />
        ))}
        {customFolders.length > 0 && <Sep />}
        <Item icon="📢" label="광고 메일함" onClick={run(() => onAutoFile(mail, "ADS", "광고 메일함"))} />
        <Item icon="🚫" label="스팸함" onClick={run(() => onAutoFile(mail, "SPAM", "스팸함"))} />
        {customFolders.length === 0 && (
          <div style={{ padding: "6px 10px", fontSize: 11.5, color: "var(--text-3)" }}>
            ‘내 메일함’을 만들면 여기에 표시됩니다
          </div>
        )}
      </SubRow>
      <Item icon="🧹" label="이 사람이 보낸 메일 모두 삭제" danger onClick={run(() => onDeleteSender(mail))} />
      <Sep />
      {multi && <div style={{ padding: "5px 11px 6px", fontSize: 11.5, color: "var(--text-3)" }}>{targetCount}개 선택됨</div>}
      {!multi && (
        <>
          <Item icon="📂" label="열기" onClick={run(() => onOpen(mail))} />
          <Item icon="🗗" label="새 창에서 열기" onClick={run(() => onNewWindow(mail))} />
          <Sep />
          <Item icon="↩️" label="답장" onClick={run(() => onReply(mail))} />
          <Item icon="📢" label="전체답장" onClick={run(() => onReplyAll(mail))} />
          <Item icon="↪️" label="전달" onClick={run(() => onForward(mail))} />
          {/* 보낸메일함에서는 같은 내용을 첨부까지 그대로 다시 보내는 일이 잦다. */}
          {mail.folder === "SENT" && (
            <Item icon="📤" label="다시 보내기" onClick={run(() => onResend(mail))} />
          )}
          <Sep />
        </>
      )}
      <Item icon={mail.is_read ? "📩" : "📖"} label={mail.is_read ? "안 읽음 표시" : "읽음 표시"} onClick={run(() => onFlag(mail, mail.is_read ? "unread" : "read"))} />
      <Item icon={mail.is_starred ? "☆" : "⭐"} label={mail.is_starred ? "별표 해제" : "별표 표시"} onClick={run(() => onFlag(mail, mail.is_starred ? "unstar" : "star"))} />
      <Item icon="❗" label={mail.is_important ? "중요 해제" : "중요 표시"} onClick={run(() => onFlag(mail, mail.is_important ? "unimportant" : "important"))} />
      <Sep />
      <SubRow icon="📁" label="이동" name="move">
        {folder !== "INBOX" && <Item icon="📥" label="받은 메일함" onClick={run(() => onMove(mail, "INBOX"))} />}
        {folder !== "ADS" && <Item icon="📢" label="광고 메일함" onClick={run(() => onMove(mail, "ADS"))} />}
        {folder !== "SPAM" && <Item icon="🚫" label="스팸함" onClick={run(() => onMove(mail, "SPAM"))} />}
        {folder !== "TRASH" && <Item icon="🗑️" label="휴지통" onClick={run(() => onMove(mail, "TRASH"))} />}
        {customFolders.length > 0 && <Sep />}
        {/* 이동 메뉴도 계층·순서를 지킨다. 지금 있는 폴더는 뺀다.
            (상위 폴더를 빼도 그 자식은 남겨야 하므로 펼친 뒤에 거른다) */}
        {flattenFolders(customFolders)
          .filter(({ folder: f }) => f.code !== folder)
          .map(({ folder: f, depth }) => (
            <Item key={f.code} icon={depth > 0 ? "└" : "📂"} indent={depth}
                  label={f.name} onClick={run(() => onMove(mail, f.code))} />
          ))}
      </SubRow>
      {labels.length > 0 && (
        <SubRow icon="🏷️" label="라벨" name="label">
          {labels.map((l) => {
            const on = mail.labels.some((x) => x.id === l.id);
            return <Item key={l.id} icon={on ? "✓" : "　"} label={l.name} onClick={run(() => onToggleLabel(mail, l.id))} />;
          })}
        </SubRow>
      )}
      {isSpam
        ? <Item icon="✅" label="스팸 아님 (받은편지함)" onClick={run(() => onMove(mail, "INBOX"))} />
        : <Item icon="🚫" label="스팸신고" onClick={run(() => onMove(mail, "SPAM"))} />}
      <Sep />
      {!multi && <Item icon="🖨️" label="인쇄" onClick={run(() => onPrint(mail))} />}
      <Item icon="🗑️" label={folder === "TRASH" ? "영구 삭제" : "삭제"} danger onClick={run(() => onDelete(mail))} />
    </div>
  );
}

// ── IMAP 외부 계정 뷰 ─────────────────────────────────────────────────────
type MailAccount = { id: number; display_name: string; email_address: string; imap_host: string; imap_port: number; imap_user: string; use_ssl: boolean; is_active: boolean; last_synced_at: string | null };

function MailAccountsView() {
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ display_name: "", email_address: "", imap_host: "", imap_port: 993, imap_user: "", imap_password: "", use_ssl: true });
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get<MailAccount[]>("/mail/accounts/me").then((r) => setAccounts(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/mail/accounts", form);
      toast.success("계정 추가됨");
      setShowForm(false);
      setForm({ display_name: "", email_address: "", imap_host: "", imap_port: 993, imap_user: "", imap_password: "", use_ssl: true });
      load();
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const del = async (id: number) => {
    try { await api.delete(`/mail/accounts/${id}`); load(); } catch {}
  };

  const [syncing, setSyncing] = useState(false);
  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ fetched: number }>("/mail/accounts/sync");
      toast.success(`${r.data.fetched}개 새 메일을 받았습니다`);
      load();
    } catch (e: any) { toast.error("동기화 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? ""); }
    finally { setSyncing(false); }
  };
  const syncOne = async (id: number) => {
    try {
      const r = await api.post<{ fetched: number }>(`/mail/accounts/${id}/sync`);
      toast.success(`${r.data.fetched}개 새 메일을 받았습니다`);
      load();
    } catch (e: any) { toast.error("동기화 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? ""); }
  };

  if (loading) return <Loading />;

  return (
    <div className="t-grid cols-1" style={{ padding: 20 }}>
      <div className="t-card" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10 }}>
        <span style={{ fontWeight: 700 }}>IMAP 외부 계정</span>
        <button className="t-btn" style={{ marginLeft: "auto" }} onClick={syncAll} disabled={syncing || accounts.length === 0}>
          {syncing ? "동기화 중…" : "↻ 전체 동기화"}
        </button>
        <button className="t-btn solid" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "취소" : "+ 계정 추가"}
        </button>
      </div>
      {showForm && (
        <div className="t-card" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxWidth: 600 }}>
          {([["display_name", "표시 이름", "text"], ["email_address", "이메일 주소", "email"], ["imap_host", "IMAP 호스트", "text"], ["imap_user", "로그인 ID", "text"], ["imap_password", "비밀번호", "password"]] as const).map(([k, label, t]) => (
            <div key={k}>
              <label className="t-label">{label}</label>
              <input type={t} className="t-input" value={(form as any)[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div>
            <label className="t-label">포트</label>
            <input type="number" className="t-input" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" id="ssl" checked={form.use_ssl} onChange={(e) => setForm({ ...form, use_ssl: e.target.checked })} />
            <label htmlFor="ssl" className="t-label" style={{ marginBottom: 0 }}>SSL/TLS</label>
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <button className="t-btn solid" onClick={save} disabled={!form.display_name || !form.email_address || !form.imap_host}>저장</button>
          </div>
        </div>
      )}
      {accounts.length === 0 && <Empty msg="연결된 외부 계정이 없습니다" />}
      {accounts.map((a) => (
        <div key={a.id} className="t-card" style={{ display: "flex", flexDirection: "row", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{a.display_name}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{a.email_address} · {a.imap_host}:{a.imap_port}{a.use_ssl ? " (SSL)" : ""}</div>
            {a.last_synced_at && <div style={{ fontSize: 11, color: "var(--text-3)" }}>마지막 동기화: {new Date(a.last_synced_at).toLocaleString("ko-KR")}</div>}
          </div>
          <button className="t-btn" style={{ fontSize: 12 }} onClick={() => syncOne(a.id)}>동기화</button>
          <button className="t-btn" style={{ fontSize: 12 }} onClick={() => del(a.id)}>삭제</button>
        </div>
      ))}
    </div>
  );
}

// ── 공용 편지함 뷰 (목록 + 열기 + 관리자 생성/멤버/비활성) ───────────────────
type SharedMailbox = { id: number; email_alias: string; name: string; dept_id: number | null; is_active: boolean; member_count: number; mailbox_user_id: number | null; member_user_ids: number[] };

function SharedMailboxesView() {
  const [mailboxes, setMailboxes] = useState<SharedMailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setParams] = useSearchParams();
  const isAdmin = useAuth((s) => !!(s.user?.roles.includes("SUPER_ADMIN") || s.user?.roles.includes("HR_ADMIN")));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{ alias: string; name: string; members: { id: number; name: string }[] }>({ alias: "", name: "", members: [] });
  const [orgOpen, setOrgOpen] = useState(false);
  const [memberPickFor, setMemberPickFor] = useState<SharedMailbox | null>(null);

  const load = () => api.get<SharedMailbox[]>("/mail/shared-mailboxes").then((r) => setMailboxes(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openBox = (m: SharedMailbox) => { if (m.mailbox_user_id) setParams({ filter: "INBOX", mbx: String(m.mailbox_user_id) }); };

  const create = async () => {
    try {
      await api.post("/mail/shared-mailboxes", { email_alias: form.alias.trim(), name: form.name.trim(), member_user_ids: form.members.map((x) => x.id) });
      toast.success("공용 편지함 생성됨", `${form.alias.includes("@") ? form.alias : form.alias + "@timple.me"}`);
      setShowForm(false); setForm({ alias: "", name: "", members: [] }); load();
    } catch (e: any) { toast.error("생성 실패", e?.response?.data?.error?.message || e?.response?.data?.detail); }
  };
  const setMembers = async (m: SharedMailbox, ids: number[]) => {
    try { await api.patch(`/mail/shared-mailboxes/${m.id}`, { member_user_ids: ids }); load(); }
    catch (e: any) { toast.error("멤버 변경 실패", e?.response?.data?.error?.message); }
  };
  const deactivate = async (m: SharedMailbox) => {
    if (!confirm(`'${m.name}' 공용 편지함을 비활성화할까요? (메일은 보존됩니다)`)) return;
    try { await api.delete(`/mail/shared-mailboxes/${m.id}`); toast.success("비활성화됨"); load(); }
    catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
  };

  if (loading) return <Loading />;

  return (
    <div className="t-grid cols-1" style={{ padding: 20 }}>
      <div className="t-card" style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 700 }}>공용 편지함</span>
        {isAdmin && (
          <button className="t-btn t-btn-primary t-btn-sm" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "취소" : "+ 새 공용 편지함"}
          </button>
        )}
      </div>

      {showForm && isAdmin && (
        <div className="t-card" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label className="t-label">주소(별칭)</label>
            <input className="t-input" placeholder="예: all 또는 tax (자동으로 @timple.me)" value={form.alias}
              onChange={(e) => setForm({ ...form, alias: e.target.value })} />
          </div>
          <div>
            <label className="t-label">이름</label>
            <input className="t-input" placeholder="예: 전체 공지함 / 세무 문의" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button className="t-btn t-btn-sm" onClick={() => setOrgOpen(true)}>👥 멤버 선택</button>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>
              {form.members.length ? form.members.map((x) => x.name).join(", ") : "멤버를 선택하세요"}
            </span>
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <button className="t-btn solid" onClick={create} disabled={!form.alias.trim() || !form.name.trim()}>생성</button>
          </div>
        </div>
      )}

      {mailboxes.length === 0 && <Empty msg={isAdmin ? "공용 편지함이 없습니다. + 새 공용 편지함으로 만드세요" : "접근 가능한 공용 편지함이 없습니다"} />}
      {mailboxes.map((m) => (
        <div key={m.id} className="t-card" style={{ display: "flex", flexDirection: "row", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>👥 {m.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{m.email_alias} · 멤버 {m.member_count}명</div>
          </div>
          <button className="t-btn t-btn-sm" onClick={() => openBox(m)} disabled={!m.mailbox_user_id}>열기</button>
          {isAdmin && <button className="t-btn t-btn-sm" onClick={() => setMemberPickFor(m)} title="멤버 다시 설정">멤버</button>}
          {isAdmin && <button className="t-btn t-btn-sm t-btn-danger" onClick={() => deactivate(m)}>비활성</button>}
        </div>
      ))}

      {orgOpen && (
        <OrgPicker title="공용 편지함 멤버 선택" multi
          onPick={(us) => setForm((f) => ({ ...f, members: us.map((u) => ({ id: u.id, name: u.name })) }))}
          onClose={() => setOrgOpen(false)} />
      )}
      {memberPickFor && (
        <OrgPicker title={`'${memberPickFor.name}' 멤버 다시 설정`} multi
          excludeIds={[]}
          onPick={(us) => { void setMembers(memberPickFor, us.map((u) => u.id)); }}
          onClose={() => setMemberPickFor(null)} />
      )}
    </div>
  );
}

// ── 주소록 (address book) ──────────────────────────────────────────────────
type ContactForm = { id?: number; name: string; email: string; organization: string; title: string; phone: string; memo: string };
const EMPTY_CONTACT: ContactForm = { name: "", email: "", organization: "", title: "", phone: "", memo: "" };
type ContactGroupBy = "recent" | "name" | "organization" | "title";
const CONTACT_TABS: { k: ContactGroupBy; label: string }[] = [
  { k: "recent", label: "최근 보낸 순" },
  { k: "name", label: "이름순" },
  { k: "organization", label: "소속별" },
  { k: "title", label: "직책별" },
];

function ContactsView() {
  const nav = useNavigate();
  const [contacts, setContacts] = useState<MailContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ContactForm>(EMPTY_CONTACT);
  // 기본은 최근 보낸 순. 주소록은 이름을 찾으려고가 아니라
  // "요즘 연락하는 사람에게 또 보내려고" 여는 일이 많다.
  const [groupBy, setGroupBy] = useState<ContactGroupBy>("recent");

  const load = (query = "") =>
    api.get<MailContact[]>(`/mail/contacts/${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setContacts(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submitSearch = (e: React.FormEvent) => { e.preventDefault(); load(q.trim()); };
  const startNew = () => { setForm(EMPTY_CONTACT); setShowForm(true); };
  const startEdit = (c: MailContact) => {
    setForm({ id: c.id, name: c.name, email: c.email, organization: c.organization || "", title: c.title || "", phone: c.phone || "", memo: c.memo || "" });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.email.trim()) { toast.warn("이메일을 입력하세요"); return; }
    try {
      if (form.id) await api.patch(`/mail/contacts/${form.id}`, form);
      else await api.post("/mail/contacts/", form);
      toast.success(form.id ? "연락처 수정됨" : "연락처 추가됨");
      setShowForm(false); setForm(EMPTY_CONTACT); load(q.trim());
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message || e?.response?.data?.detail); }
  };
  const del = async (c: MailContact) => {
    if (!confirm(`"${c.name}" 연락처를 삭제할까요?`)) return;
    try { await api.delete(`/mail/contacts/${c.id}`); load(q.trim()); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };
  const writeTo = (c: MailContact) => nav("/mail/compose", { state: { initial: { to_addrs: [c.email] } } });

  // 구분 탭 기준으로 정렬·그룹핑
  const groups = useMemo(() => {
    const byName = (a: MailContact, b: MailContact) => (a.name || a.email).localeCompare(b.name || b.email, "ko");
    // 서버가 최근 보낸 순으로 정렬해 주므로 여기서 다시 섞지 않는다.
    // (예전엔 무조건 이름순으로 다시 정렬해 서버 정렬이 무시됐다)
    if (groupBy === "recent") return [{ key: "", items: contacts }];
    if (groupBy === "name") return [{ key: "", items: [...contacts].sort(byName) }];
    const map = new Map<string, MailContact[]>();
    for (const c of contacts) {
      const key = ((groupBy === "organization" ? c.organization : c.title) || "(미지정)").trim() || "(미지정)";
      (map.get(key) ?? map.set(key, []).get(key)!).push(c);
    }
    return [...map.keys()].sort((a, b) => a.localeCompare(b, "ko"))
      .map((k) => ({ key: k, items: map.get(k)!.sort(byName) }));
  }, [contacts, groupBy]);

  if (loading) return <Loading />;

  return (
    <div className="mail-wrap">
      <div className="t-page-header" style={{ padding: "20px 24px 10px" }}>
        <div>
          <h1>주소록</h1>
          <div className="subtitle">자주 쓰는 주소를 저장해 메일 작성 시 자동완성으로 사용합니다 · {contacts.length}명</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="t-btn t-btn-primary" onClick={startNew}><I.Plus size={14}/> 연락처 추가</button>
        </div>
      </div>

      <div style={{ padding: "0 24px 8px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <form onSubmit={submitSearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 220, maxWidth: 380 }}>
          <input className="t-input" placeholder="이름 또는 이메일 검색" value={q}
            onChange={(e) => setQ(e.target.value)} onBlur={() => load(q.trim())} style={{ flex: 1 }}/>
          <button className="t-btn" type="submit">검색</button>
        </form>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 700 }}>구분</span>
          {CONTACT_TABS.map((t) => (
            <button key={t.k} className={`mail-filter-chip ${groupBy === t.k ? "active" : ""}`} onClick={() => setGroupBy(t.k)}>{t.label}</button>
          ))}
        </div>
      </div>

      {showForm && (
        <div style={{ padding: "0 24px 10px" }}>
          <div className="t-card" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxWidth: 680 }}>
            <div><label className="t-label">이름</label>
              <input className="t-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="이름 (비우면 이메일 ID)"/></div>
            <div><label className="t-label">이메일 *</label>
              <input className="t-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com"/></div>
            <div><label className="t-label">소속(회사/부서)</label>
              <input className="t-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}/></div>
            <div><label className="t-label">직책</label>
              <input className="t-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="예: 팀장, 대리"/></div>
            <div><label className="t-label">전화</label>
              <input className="t-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}/></div>
            <div><label className="t-label">메모</label>
              <input className="t-input" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}/></div>
            <div style={{ gridColumn: "span 2", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="t-btn" onClick={() => { setShowForm(false); setForm(EMPTY_CONTACT); }}>취소</button>
              <button className="t-btn solid t-btn-primary" onClick={save} disabled={!form.email.trim()}>{form.id ? "수정" : "추가"}</button>
            </div>
          </div>
        </div>
      )}

      {/* 컬럼 헤더 */}
      <div className="contacts-colhead">
        <span/><span>이름</span><span>소속</span><span>직책</span><span>이메일</span><span>전화</span><span>최근 발송</span><span/>
      </div>

      <div className="contacts-scroll">
        {contacts.length === 0 && <Empty msg="주소록이 비어 있습니다. 연락처를 추가하거나, 메일 확인 화면에서 발신자를 추가하세요" />}
        {groups.map((g) => (
          <div key={g.key || "__all__"}>
            {g.key && <div className="contacts-group-head">{g.key} · {g.items.length}명</div>}
            {g.items.map((c) => (
              <div key={c.id} className="contact-row">
                <div className="av" style={{ background: colorOf(c.email) }}>{initials(c.name || c.email)}</div>
                <span className="cell" style={{ fontWeight: 600 }}>{c.name}</span>
                <span className="cell" style={{ color: "var(--text-2)" }}>{c.organization || "-"}</span>
                <span className="cell" style={{ color: "var(--text-2)" }}>{c.title || "-"}</span>
                <span className="cell" style={{ color: "var(--text-3)" }}>{c.email}</span>
                <span className="cell" style={{ color: "var(--text-3)" }}>{c.phone || "-"}</span>
                {/* 정렬 근거를 보여 준다 — 왜 이 순서인지 알 수 있어야 한다. */}
                <span className="cell" style={{ color: "var(--text-3)", fontSize: 11.5 }}
                      title={c.last_sent_at ? `마지막 발송: ${fmtFullDate(c.last_sent_at)}` : "보낸 적 없음"}>
                  {c.last_sent_at ? fmtDate(c.last_sent_at) : "-"}
                </span>
                <span className="actions">
                  <button className="t-btn t-btn-sm t-btn-primary" onClick={() => writeTo(c)}>메일</button>
                  <button className="t-btn t-btn-sm" onClick={() => startEdit(c)}>편집</button>
                  <button className="t-btn t-btn-sm t-btn-danger" onClick={() => del(c)}>삭제</button>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


/** 행에 마우스를 올렸을 때 뜨는 빠른 미리보기.
 *
 *  메일을 열면 읽음으로 바뀌므로 "열지 않고 훑어보는" 창이 따로 있어야 한다.
 *  화면 끝에서 잘리지 않도록 좌표를 접어 준다. 카드는 마우스를 받지 않아
 *  (pointer-events:none) 아래 행의 클릭을 가로채지 않는다. */
function MailPeek({ peek, isOut, namesOf, senderName }: {
  peek: { mail: Mail; x: number; y: number };
  isOut: boolean;
  namesOf: (list: string[]) => string;
  senderName: (addr: string) => string;
}) {
  const W = 380, H = 240, GAP = 16;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const left = peek.x + GAP + W > vw ? Math.max(8, peek.x - GAP - W) : peek.x + GAP;
  const top = peek.y + GAP + H > vh ? Math.max(8, peek.y - GAP - H) : peek.y + GAP;
  const m = peek.mail;
  const when = m.received_at || m.sent_at || m.created_at;
  return (
    <div className="mail-peek" style={{ left, top }} aria-hidden>
      <div className="pk-subject">{m.subject || "(제목 없음)"}</div>
      <div className="pk-meta">
        {isOut ? `\u2192 ${namesOf(m.to_addrs) || "(받는 사람 없음)"}` : senderName(m.from_addr)}
        {when ? ` \u00b7 ${new Date(when).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
        {m.has_attachments ? " \u00b7 첨부 있음" : ""}
      </div>
      <div className="pk-body">{m.preview}</div>
      <div className="pk-hint">클릭하면 메일이 열립니다(읽음으로 바뀝니다).</div>
    </div>
  );
}
